<template>
  <div class="flex flex-col gap-30u">
    <form
      v-if="step === 'otp'"
      ref="otpForm"
      class="space-y-4"
      @submit.prevent="submitOtp"
    >
      <div class="flex flex-col gap-4">
        <Label
          for="pin-input"
          class="text-basics-primary text-xs flex justify-between sr-only"
        >
          <span :class="{ 'text-basics-accent-red': error }">{{
            $t("channel.auth.phone.code.label")
          }}</span>
          <span class="opacity-60">{{
            $t("channel.auth.phone.code.required")
          }}</span>
        </Label>

        <PinInput
          id="pin-input"
          v-model="otp"
          class="mt-1"
          :error="!!error"
          :aria-label="$t('channel.auth.phone.code.label')"
          @complete="submitOtp"
        />

        <p
          v-if="error"
          role="alert"
          class="text-basics-accent-red font-sans font-medium text-sm"
        >
          <Icon icon="mdi:info-outline" class="inline-block size-5 mb-[2px]" />
          {{ error }}
        </p>

        <p class="text-text text-center text-lg font-medium">
          {{ $t("channel.auth.phone.code.notReceived") }}
        </p>

        <ButtonV2
          variant="subtle"
          size="lg"
          type="button"
          class="disabled:text-text-disabled !font-sans"
          :disabled="!!isCountdownActive"
          @click="resendPhoneNumber"
        >
          {{
            $t("channel.auth.phone.code.resend", countdownSeconds ? 0 : 1, {
              named: {
                countdown: formatCountdown(countdownSeconds ?? 0),
              },
            })
          }}
        </ButtonV2>
      </div>

      <ButtonV2
        type="submit"
        variant="primary"
        size="lg"
        class="w-full mt-30u"
        :disabled="loading || otp.length !== 6"
      >
        <span v-if="loading" class="loading loading-spinner loading-md" />
        {{ $t("channel.auth.phone.code.continue") }}
      </ButtonV2>
    </form>

    <form v-else class="space-y-4" @submit.prevent="submitPhoneNumber">
      <PhoneInput
        ref="phoneInput"
        :country-code="countryCodeField.state.value.value as CountryCode"
        :phone-number="phoneNumberField.state.value.value"
        :error="phoneNumberField.state.value.meta.errors[0]?.message || error"
        @update:country-code="
          (val) => {
            countryCodeField.api.handleChange(val ?? '');
            clearFieldErrors('phoneNumber');
          }
        "
        @update:phone-number="
          (val) => {
            phoneNumberField.api.handleChange(val ?? '');
            clearFieldErrors('phoneNumber');
          }
        "
      />

      <ButtonV2
        role="button"
        variant="primary"
        size="lg"
        class="w-full mt-30u"
        :disabled="loading || !!isCountdownActive"
        @click="submitPhoneNumber"
      >
        {{
          isCountdownActive
            ? $t("channel.auth.phone.code.countdown", {
                countdown: formatCountdown(props.countdownSeconds ?? 0),
              })
            : $t("channel.auth.phone.code.send")
        }}
      </ButtonV2>
    </form>

    <template v-if="$slots.authOptions">
      <div class="flex items-center justify-center gap-2">
        <div class="h-1u w-full bg-border" />
        <span class="text-text-secondary text-center font-serif text-sm">
          {{ $t("channel.auth.phone.or") }}
        </span>
        <div class="h-1u w-full bg-border" />
      </div>

      <slot name="authOptions" />
    </template>

    <span
      class="align-self-stretch text-text-secondary text-center font-sans text-sm font-normal"
    >
      <i18n-t keypath="channel.auth.terms.agreement" tag="span">
        <template #terms>
          <a class="underline" href="/terms-of-service">
            {{ $t("channel.auth.terms.termsLink") }}
          </a>
        </template>
        <template #privacy>
          <a class="underline" href="/privacy-policy">
            {{ $t("channel.auth.terms.privacyLink") }}
          </a>
        </template>
      </i18n-t>
    </span>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
  useTemplateRef,
  watch,
  watchEffect,
} from "vue";
import PhoneInput from "@/components/ui/phone-number/phone-input.vue";
import {
  defaultInternationalPhoneNumber,
  type CountryCode,
} from "@/constants/country";
import { Label } from "reka-ui";
import PinInput from "@/components/ui/pin-input/pin-input.vue";
import { Icon } from "@iconify/vue";
import { useLocation } from "@/composables/use-location";
import phone from "phone";
import { useForm } from "@tanstack/vue-form";
import { z } from "zod/v4";
import { trimTestPhoneNumberPart } from "@/utils/format";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useUserStore } from "@/stores/user.store";
import { useI18n } from "vue-i18n";

const step = defineModel<"phoneNumber" | "otp">("step", { required: true });

const emit = defineEmits<{
  submitPhoneNumber: [fullPhoneNumber: string];
  submitOtp: [{ fullPhoneNumber: string; otp: string }];
}>();

const props = defineProps<{
  loading: boolean;
  error: string | null;
  countdownSeconds?: number;
}>();

const isCountdownActive = computed(() => {
  return props.countdownSeconds && props.countdownSeconds > 0;
});

const { t } = useI18n();
const otpFormRef = useTemplateRef("otpForm");
const phoneInputRef = useTemplateRef("phoneInput");

watchEffect(() => {
  if (phoneInputRef.value) {
    phoneInputRef.value.focus();
  }
});

const schema = z
  .object({
    countryCode: z.string(),
    phoneNumber: z
      .string()
      .min(1, t("channel.auth.phone.code.phoneNumberRequired")),
  })
  .refine(
    (values) =>
      values.phoneNumber.length === 0 ||
      phone(trimTestPhoneNumberPart(values.phoneNumber).real, {
        country: values.countryCode,
      }).isValid,
    {
      error: t("channel.auth.phone.code.invalidPhoneNumber"),
      path: ["phoneNumber"],
    },
  );

const form = useForm({
  defaultValues: {
    countryCode: defaultInternationalPhoneNumber.countryCode as string,
    phoneNumber: defaultInternationalPhoneNumber.number,
  },
  validators: {
    onSubmit: schema,
  },
  onSubmit: async ({ value }) => {
    const { real, test } = trimTestPhoneNumberPart(value.phoneNumber);
    const e164 = phone(real, {
      country: value.countryCode,
    });

    phoneNumberForOtp.value = e164.phoneNumber
      ? e164.phoneNumber.concat(test)
      : null;

    if (!phoneNumberForOtp.value) return;
    emit("submitPhoneNumber", phoneNumberForOtp.value);
  },
});

const countryCodeField = form.useField({
  name: "countryCode",
  validators: { onChange: schema.shape.countryCode },
});

const phoneNumberField = form.useField({
  name: "phoneNumber",
});

const numberPartValue = form.useStore((state) => state.values.phoneNumber);

function clearFieldErrors(fieldName: string) {
  form.setFieldMeta(fieldName as "countryCode" | "phoneNumber", (prev) => ({
    ...prev,
    errorMap: {
      onChange: undefined,
      onSubmit: undefined,
    },
    errors: [],
  }));
}

const phoneNumberForOtp = ref<string | null>(null);

const {
  countryCode: locationCountryCode,
  fetchLocation,
  watchLocationCountry,
} = useLocation();

onMounted(() => {
  fetchLocation();

  if (locationCountryCode.value) {
    form.setFieldValue("countryCode", locationCountryCode.value);
  }
});

watchLocationCountry((countryCode) => {
  if (numberPartValue.value.length > 0) return;
  form.setFieldValue("countryCode", countryCode);
});

const otp = ref("");

watchEffect(() => {
  const element = otpFormRef.value;
  if (!element) return;

  (
    element.querySelector(
      '[autocomplete="one-time-code"]',
    ) as HTMLElement | null
  )?.focus();
});

function resendPhoneNumber() {
  if (isCountdownActive.value) return;
  otp.value = "";
  submitPhoneNumber();
}

function submitPhoneNumber() {
  form.handleSubmit();
}

function submitOtp() {
  if (!phoneNumberForOtp.value) return;
  emit("submitOtp", {
    fullPhoneNumber: phoneNumberForOtp.value,
    otp: otp.value,
  });
}

watch(step, (newStep) => {
  if (newStep === "phoneNumber") {
    otp.value = "";
  }
});

const userStore = useUserStore();
function formatCountdown(seconds: number): string {
  return new Intl.RelativeTimeFormat(userStore.userLocale, {
    numeric: "always",
    style: "long",
  }).format(seconds, "seconds");
}
</script>
